import log from "loglevel";
import { Dispatch } from "react";
import { useLocalStorage, useSessionStorage } from "usehooks-ts";

export interface JsonItemStorage<T> {
  key: string;
  set: (value: T) => void;
  get: () => T;
  use: () => [T, Dispatch<T>, () => void];
}

const makeStorage = <T>(
  key: string,
  initialValue: T,
  storage: Storage,
  hook: (key: string, initialValue: T) => [T, Dispatch<T>, () => void],
) => ({
  key,
  get: () => {
    try {
      const item = storage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (e) {
      log.error(e);
      return initialValue;
    }
  },
  set: (value: T | undefined) => {
    if (value) {
      storage.setItem(key, JSON.stringify(value));
    } else {
      storage.removeItem(key);
    }
    // For useLocalStorage hook
    window.dispatchEvent(new Event("local-storage"));
  },
  use: () => hook(key, initialValue),
});

export const makeJsonItemLocalStorage = <T>(
  key: string,
  initialValue: T,
): JsonItemStorage<T> =>
  makeStorage<T>(key, initialValue, localStorage, useLocalStorage);

export const makeJsonItemSessionStorage = <T>(
  key: string,
  initialValue: T,
): JsonItemStorage<T> =>
  makeStorage<T>(key, initialValue, sessionStorage, useSessionStorage);

export const clearLocalStorage = () => {
  localStorage.clear();
  // For useLocalStorage hook
  window.dispatchEvent(new Event("local-storage"));
};
